import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Search, ChevronRight, Download, Upload, Users, X } from "lucide-react";
import { Avatar } from "@/shared/ui/Avatar";
import { Input } from "@/shared/ui/Input";
import { StatusBadge } from "@/shared/ui/StatusBadge";
import { Pagination } from "@/shared/ui/Pagination";
import { MobileSort, type SortOption } from "@/shared/ui/MobileSort";
import { SortHeader } from "@/shared/ui/SortHeader";
import { useDebounce } from "@/shared/lib/useDebounce";
import { relativeTime } from "@/shared/lib/time";
import { useShell } from "@/app/shellContext";
import { useCustomers } from "./hooks";
import { CustomerDrawer } from "./CustomerDrawer";
import {
  PAGE_SIZE,
  type CustomerListItem,
  type CustomerOrdering,
  type CustomerSortField,
  type CustomerStatus,
} from "./types";

const STATUS_FILTERS: Array<CustomerStatus> = ["active", "inactive", "new"];
const TH = "border-b border-grey-200 bg-grey-50 px-4 py-[11px] text-[12px] font-semibold text-grey-500";

export function CustomersScreen() {
  const { t } = useTranslation();
  const { setCustomerCount } = useShell();

  const [searchInput, setSearchInput] = useState("");
  const [status, setStatus] = useState<CustomerStatus | "all">("all");
  const [page, setPage] = useState(1);
  // null = backend default ordering (status-first, newest registration) until a header is clicked.
  const [ordering, setOrdering] = useState<CustomerOrdering | null>(null);
  const [openId, setOpenId] = useState<number | null>(null);

  const search = useDebounce(searchInput, 350);

  // Reset to page 1 whenever the query narrows.
  useEffect(() => {
    setPage(1);
  }, [search, status, ordering]);

  const { data, isLoading, isError, isFetching } = useCustomers({ search, status, page, ordering });

  const count = data?.count ?? 0;
  const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));

  // Publish count to the sidebar nav pill.
  useEffect(() => {
    setCustomerCount(data?.count);
    return () => setCustomerCount(undefined);
  }, [data?.count, setCustomerCount]);

  const rows = data?.results ?? [];
  const rangeFrom = count === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const rangeTo = Math.min(page * PAGE_SIZE, count);

  const sort = { ordering: ordering ?? "status_order", onSortChange: setOrdering, className: TH };

  // Same fields/first directions as the table headers — the phone card list has no headers.
  const sortOptions: SortOption<CustomerSortField>[] = [
    { field: "status_order", label: t("customers.colStatus"), firstDir: "asc" },
    { field: "first_name", label: t("customers.colCustomer"), firstDir: "asc" },
    { field: "phone_number", label: t("customers.colPhone"), firstDir: "asc" },
    { field: "loans_count", label: t("customers.sortContracts") },
    { field: "last_login_at", label: t("customers.colActivity") },
  ];

  // A filter is active when the operator narrowed the list — drives the empty-state reset CTA.
  const hasFilters = searchInput.trim() !== "" || status !== "all";
  function clearFilters() {
    setSearchInput("");
    setStatus("all");
  }

  return (
    <div>
      {/* Toolbar */}
      <div className="mb-4 flex flex-wrap items-center gap-2.5">
        <div className="relative w-full sm:w-[320px]">
          <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-grey-400" />
          {/* pl-9 keeps space for the leading Search icon; h-[38px] matches toolbar height. */}
          <Input
            type="search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder={t("customers.searchPlaceholder")}
            aria-label={t("customers.searchLabel")}
            className="h-[38px] pl-9 pr-3"
          />
        </div>

        {/* Status filter as visible chips — one click vs a buried dropdown (replaces the old select). */}
        <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label={t("customers.colStatus")}>
          <FilterChip selected={status === "all"} onClick={() => setStatus("all")}>
            {t("customers.statusAll")}
          </FilterChip>
          {STATUS_FILTERS.map((s) => (
            <FilterChip key={s} selected={status === s} onClick={() => setStatus(s)}>
              {t(`status.${s}`)}
            </FilterChip>
          ))}
        </div>

        {/* Phones: the count shares a row with the sort picker; md:contents keeps the desktop row as-is. */}
        <div className="flex w-full items-center gap-3 md:contents">
          <span className="tnum flex-none text-[13.5px] font-medium text-grey-500">
            {t("customers.count", { count })}
          </span>
          <MobileSort
            options={sortOptions}
            ordering={sort.ordering}
            onSortChange={setOrdering}
            className="ml-auto w-[210px] md:hidden"
          />
        </div>

        <div className="hidden flex-1 md:block" />

        {/* Import / Export — not wired yet. Rendered as plainly disabled so they never
            masquerade as active actions (the old Import button used an active-green fill).
            Hidden on phones: two dead buttons would only crowd the toolbar there. */}
        <button
          type="button"
          disabled
          aria-disabled="true"
          className="hidden h-[38px] cursor-not-allowed items-center gap-2 rounded-control border border-grey-200 bg-grey-50 px-3.5 text-[13.5px] font-semibold text-grey-400 md:flex"
        >
          <Download size={16} />
          {t("customers.export")}
        </button>
        <button
          type="button"
          disabled
          aria-disabled="true"
          className="hidden h-[38px] cursor-not-allowed items-center gap-2 rounded-control border border-grey-200 bg-grey-50 px-3.5 text-[13.5px] font-semibold text-grey-400 md:flex"
        >
          <Upload size={16} />
          {t("customers.import")}
        </button>
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-card border border-grey-200 bg-white shadow-card">
        {/* Phones: tappable cards — the 6-column table would need sideways scrolling there. */}
        <ul className="divide-y divide-grey-100 md:hidden">
          {isError ? (
            <li className="px-4 py-12 text-center text-[13.5px] text-red">{t("customers.loadError")}</li>
          ) : isLoading ? (
            Array.from({ length: 6 }, (_, i) => <CustomerCardSkeleton key={i} />)
          ) : rows.length === 0 ? (
            <li>
              <CustomersEmpty onClear={hasFilters ? clearFilters : undefined} />
            </li>
          ) : (
            rows.map((c) => <CustomerCard key={c.id} customer={c} onOpen={setOpenId} />)
          )}
        </ul>

        {/* Horizontal scroll on narrow viewports so columns are never clipped by the card. */}
        <div className="hidden overflow-x-auto md:block">
        <table className="w-full min-w-[680px] border-collapse">
          <thead>
            <tr>
              {/* The default backend ordering is status-first, so the Status arrow shows until a click. */}
              <SortHeader {...sort} field="first_name" firstDir="asc" label={t("customers.colCustomer")} />
              <SortHeader {...sort} field="phone_number" firstDir="asc" label={t("customers.colPhone")} />
              <SortHeader {...sort} field="loans_count" align="right" label={t("customers.colContracts")} />
              <SortHeader {...sort} field="status_order" firstDir="asc" label={t("customers.colStatus")} />
              <SortHeader {...sort} field="last_login_at" label={t("customers.colActivity")} />
              <th className={TH} aria-hidden />
            </tr>
          </thead>
          <tbody>
            {isError ? (
              <tr>
                <td colSpan={6} className="px-4 py-12 text-center text-[13.5px] text-red">
                  {t("customers.loadError")}
                </td>
              </tr>
            ) : isLoading ? (
              // Column-matched skeleton: 6 cells matching real column layout.
              Array.from({ length: PAGE_SIZE }).map((_, i) => (
                <tr key={i} className="border-b border-grey-100 last:border-0">
                  {/* Col 1: avatar circle + name bar */}
                  <td className="px-4 py-[13px]">
                    <div className="flex items-center gap-[11px]">
                      <div className="h-8 w-8 animate-pulse rounded-pill bg-grey-100" />
                      <div className="h-3 w-28 animate-pulse rounded bg-grey-100" />
                    </div>
                  </td>
                  {/* Col 2: phone */}
                  <td className="px-4 py-[13px]">
                    <div className="h-3 w-24 animate-pulse rounded bg-grey-100" />
                  </td>
                  {/* Col 3: contracts count (right-aligned) */}
                  <td className="px-4 py-[13px]">
                    <div className="ml-auto h-3 w-8 animate-pulse rounded bg-grey-100" />
                  </td>
                  {/* Col 4: status badge */}
                  <td className="px-4 py-[13px]">
                    <div className="h-5 w-16 animate-pulse rounded-pill bg-grey-100" />
                  </td>
                  {/* Col 5: activity */}
                  <td className="px-4 py-[13px]">
                    <div className="h-3 w-20 animate-pulse rounded bg-grey-100" />
                  </td>
                  {/* Col 6: chevron — empty */}
                  <td className="px-4 py-[13px]" />
                </tr>
              ))
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={6} className="p-0">
                  <CustomersEmpty onClear={hasFilters ? clearFilters : undefined} />
                </td>
              </tr>
            ) : (
              rows.map((c) => {
                const name = `${c.first_name} ${c.last_name}`.trim();
                return (
                  <tr
                    key={c.id}
                    onClick={() => setOpenId(c.id)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setOpenId(c.id);
                      }
                    }}
                    aria-label={t("customers.openCustomer", { name })}
                    className="group cursor-pointer border-b border-grey-100 transition-colors last:border-0 hover:bg-grey-50 active:bg-grey-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green/40 focus-visible:ring-inset"
                  >
                    <td className="px-4 py-[13px]">
                      <div className="flex items-center gap-[11px]">
                        <Avatar name={name} size="sm" />
                        <div>
                          <div className="flex items-center gap-2">
                            <b className="whitespace-nowrap text-[13.5px] font-semibold text-grey-900">{name}</b>
                            {c.is_premium && (
                              <span className="flex-none rounded-pill bg-green px-1.5 py-[1px] text-[10.5px] font-bold leading-[1.4] text-white">
                                {t("customers.premiumBadge")}
                              </span>
                            )}
                          </div>
                          <span className="tnum text-[12px] text-grey-400">
                            NS-{String(c.id).padStart(5, "0")}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td className="tnum px-4 py-[13px] text-[13.5px] text-grey-600">
                      {c.phone_number}
                    </td>
                    <td className="tnum px-4 py-[13px] text-right text-[13.5px] text-grey-700">
                      {c.loans_count}
                    </td>
                    <td className="px-4 py-[13px]">
                      <StatusBadge status={c.status} />
                    </td>
                    <td className="px-4 py-[13px] text-[13.5px] text-grey-500">
                      {relativeTime(c.last_login_at ?? c.last_login, t)}
                    </td>
                    <td className="px-4 py-[13px]">
                      <div className="flex items-center justify-end gap-1 text-grey-400 group-hover:text-green-dark">
                        {t("customers.edit")}
                        <ChevronRight size={18} />
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 border-t border-grey-200 bg-grey-25 px-4 py-3">
          <span className="tnum min-w-0 text-[13px] text-grey-500">
            {t("customers.rangeShown", { from: rangeFrom, to: rangeTo, total: count })}
          </span>
          <Pagination page={page} totalPages={totalPages} onChange={setPage} isFetching={isFetching} />
        </div>
      </div>

      <CustomerDrawer customerId={openId} onClose={() => setOpenId(null)} />
    </div>
  );
}

// Composed empty state: icon + title + hint. `onClear` is passed only when filters caused the
// empty result, so the operator gets a one-click way back to the full list.
function CustomersEmpty({ onClear }: { onClear?: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="px-4 py-12 text-center">
      <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-pill bg-grey-50 text-grey-400">
        <Users size={28} />
      </div>
      <div className="text-[14px] font-semibold text-grey-700">{t("customers.empty")}</div>
      <div className="mt-1 text-[13px] text-grey-500">{t("customers.emptyHint")}</div>
      {onClear && (
        <button
          type="button"
          onClick={onClear}
          className="mt-3 inline-flex items-center gap-1.5 rounded-control border border-grey-300 bg-white px-3 py-1.5 text-[13px] font-semibold text-grey-700 transition active:scale-95 hover:border-green hover:text-green"
        >
          <X size={14} strokeWidth={2} />
          {t("customers.clearFilters")}
        </button>
      )}
    </div>
  );
}

// Phone row: identity + status on top, phone + last activity below, then ID · contracts.
function CustomerCard({ customer: c, onOpen }: { customer: CustomerListItem; onOpen: (id: number) => void }) {
  const { t } = useTranslation();
  const name = `${c.first_name} ${c.last_name}`.trim();
  return (
    <li>
      <button
        type="button"
        onClick={() => onOpen(c.id)}
        className="flex w-full items-center gap-3 px-4 py-3.5 text-left transition-colors active:bg-grey-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-green/40"
      >
        <Avatar name={name} size="md" />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <b className="min-w-0 truncate text-[14px] font-semibold text-grey-900">{name}</b>
            {c.is_premium && (
              <span className="flex-none rounded-pill bg-green px-1.5 py-[1px] text-[10.5px] font-bold leading-[1.4] text-white">
                {t("customers.premiumBadge")}
              </span>
            )}
            <span className="ml-auto flex-none">
              <StatusBadge status={c.status} />
            </span>
          </div>
          <div className="mt-0.5 flex items-baseline justify-between gap-3">
            <span className="tnum flex-none text-[13px] text-grey-600">{c.phone_number}</span>
            <span className="truncate text-[12px] text-grey-500">
              {relativeTime(c.last_login_at ?? c.last_login, t)}
            </span>
          </div>
          <div className="tnum mt-1 flex items-center gap-1.5 text-[12px] text-grey-400">
            <span>NS-{String(c.id).padStart(5, "0")}</span>
            <span aria-hidden>·</span>
            <span>{t("customers.cardContracts", { count: c.loans_count })}</span>
          </div>
        </div>
        <ChevronRight size={18} className="flex-none text-grey-300" />
      </button>
    </li>
  );
}

function CustomerCardSkeleton() {
  return (
    <li className="flex items-center gap-3 px-4 py-3.5">
      <div className="h-10 w-10 flex-none animate-pulse rounded-pill bg-grey-100" />
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-3">
          <div className="h-3.5 w-32 animate-pulse rounded bg-grey-100" />
          <div className="h-5 w-14 animate-pulse rounded-pill bg-grey-100" />
        </div>
        <div className="mt-2 h-3 w-28 animate-pulse rounded bg-grey-100" />
        <div className="mt-2 h-3 w-40 animate-pulse rounded bg-grey-100" />
      </div>
    </li>
  );
}

// Segmented status filter button. Active = solid green; idle = bordered white.
function FilterChip({
  selected,
  onClick,
  children,
}: {
  selected: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={`h-[38px] rounded-pill border px-3.5 text-[13px] font-semibold transition active:scale-95 ${
        selected
          ? "border-green bg-green text-white"
          : "border-grey-300 bg-white text-grey-700 hover:bg-grey-50"
      }`}
    >
      {children}
    </button>
  );
}
